/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { css } from '@emotion/react';

import { useTranslation } from 'react-i18next';

import { Link } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import Text from '@mui/material/Typography';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import StreamOutlinedIcon from '@mui/icons-material/StreamOutlined';

import { useFolderActions } from '../../../actions/FolderActions';
import { showTemplateDialog } from '../../../utils/dialog';
import { useIsMobile } from '../../../utils/hooks';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

import Checkbox from '@mui/material/Checkbox';
import MoreMenu from './MoreMenu';

export default function Folder({
  compact,
  singleSelect = false,
  name,
  file_count,
  isModal,
  hideOptions,
  deletePermission,
  onLocationChange,
  onSelectionChange,
  selectedAssets,
  folderPrefix,
  folderCounter,
  width,
  disabled = false,
  ...folder
}) {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { updateFolder } = useFolderActions(folder?.id);

  function handleDelete() {
    showTemplateDialog('deleteFolder', {
      folder: {
        ...folder,
        name,
      },
      isProject: false,
      empty: file_count === 0,
    });
  }

  function handleEdit() {
    showTemplateDialog('editFolderName', {
      folder: {
        ...folder,
        name,
      },
    });
  }

  function handleMove({ folder_prefix }) {
    updateFolder({ folder_prefix });
  }

  const selected = useMemo(() => {
    const prefix = `${folder.folder_prefix}${folder.id}`;
    const counter = folderCounter[prefix];
    if (!counter) {
      return {
        checked: false,
        indeterminate: false,
      };
    }
    return {
      checked: !!counter.total && counter.total === counter.selected,
      indeterminate: counter.selected > 0 && counter.selected < counter.total,
    };
  }, [folderCounter, folder.folder_prefix, folder.id]);

  if (compact) {
    return (
      <TableRow
        css={css`
          cursor: pointer;
          text-decoration: none;
          color: var(--text-primary);
        `}
        onClick={() => {
          if (onLocationChange && isModal) {
            onLocationChange(folder.folder_prefix + folder.id);
          }
        }}
      >
        <TableCell>
          <Link
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              text-decoration: none;
              color: var(--text-primary);
            `}
            to={isModal ? '#' : `/folders${folder.folder_prefix}${folder.id}`}
          >
            {isModal && !singleSelect ? (
              <Checkbox
                css={css`
                  padding: 0px;
                `}
                checked={selected.checked}
                indeterminate={selected.indeterminate}
                onClick={(e) => e.stopPropagation()}
                disabled={file_count === 0}
                onChange={(e) => {
                  e.stopPropagation();
                  if (typeof onSelectionChange === 'function') {
                    onSelectionChange({
                      folderPrefix: folder.folder_prefix + folder.id,
                      type: 'folder',
                      id: folder.id,
                    });
                  }
                }}
              />
            ) : null}
            {folder.is_case ? (
              <StreamOutlinedIcon
                sx={{ color: theme.palette.secondary.main }}
              />
            ) : (
              <FolderOutlinedIcon color="primary" />
            )}
            <Text
              variant="subtitle2"
              fontWeight="600"
              css={css`
                margin-left: 8px;
              `}
            >
              {name}
            </Text>
          </Link>
        </TableCell>
        <TableCell></TableCell>
        <TableCell>
          {new Date(folder.updated_at).toLocaleDateString(i18n.language)}
        </TableCell>
        {!isModal && !hideOptions ? (
          <TableCell
            css={css`
              text-align: right;
            `}
          >
            <MoreMenu
              deletePermission={deletePermission}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMove={handleMove}
              folderPrefix={folder.folder_prefix}
              type="folder"
              showMove={false}
              isCompact
            />
          </TableCell>
        ) : null}
      </TableRow>
    );
  }

  return (
    <Link
      to={
        !isModal
          ? folder.is_case
            ? `/cases/${folder.id}`
            : `/folders${folder.folder_prefix}${folder.id}`
          : '#'
      }
      css={css`
        display: flex;
        align-items: center;
        gap: ${isMobile ? '8px' : '16px'};
        width: ${width}px;
        min-width: ${width}px;
        /*
        width: isMobile ? '160px' : '239px';
        */

        text-decoration: none;
        color: var(--text-primary);
      `}
      onClick={() => {
        if (onLocationChange && isModal) {
          onLocationChange(folder.folder_prefix + folder.id);
        }
      }}
    >
      {isModal && !singleSelect ? (
        <Checkbox
          css={css`
            padding: 0px;
          `}
          checked={selected.checked}
          indeterminate={selected.indeterminate}
          onClick={(e) => e.stopPropagation()}
          disabled={file_count === 0}
          onChange={(e) => {
            e.stopPropagation();
            if (typeof onSelectionChange === 'function') {
              onSelectionChange({
                folderPrefix: folder.folder_prefix + folder.id,
                type: 'folder',
                id: folder.id,
              });
            }
          }}
        />
      ) : null}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;

          height: 64px;
          width: 64px;
          min-width: 64px;

          background-color: ${folder.is_case
            ? theme.palette.secondary.light
            : '#dce8ff'};
          border-radius: 8px;
          padding: 8px;
        `}
      >
        {folder.is_case ? (
          <StreamOutlinedIcon color={theme.palette.secondary.contrastText} />
        ) : (
          <FolderOutlinedIcon color="primary" />
        )}
        <Text
          css={css`
            align-self: end;
          `}
          color={
            folder.is_case ? theme.palette.secondary.contrastText : 'primary'
          }
          fontWeight="600"
        >
          {file_count}
        </Text>
      </div>
      <Text
        variant="subtitle2"
        ellipsis="true"
        noWrap
        css={css`
          max-width: ${isMobile ? '60px' : '148px'};
        `}
        fontWeight="600"
      >
        {folder.user_fallback ? t('Övrigt') : name}
      </Text>
      {!hideOptions ? (
        <div
          css={css`
            padding-top: 0.125rem;
            margin-left: auto;
          `}
        >
          <MoreMenu
            deletePermission={deletePermission}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMove={handleMove}
            folderPrefix={folder.folder_prefix}
            type="folder"
            showMove={false}
          />
        </div>
      ) : null}
    </Link>
  );
}
